import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";

type WebhookDelivery = components["schemas"]["WebhookDeliveryResponse"];
type WebhookDeliveryStatus = NonNullable<components["schemas"]["WebhookDeliveryStatus"]>;

interface WebhookDeliveriesTableProps {
  clientId?: string;
  status?: WebhookDeliveryStatus;
  sinceDays: number;
  limit: number;
  /** Bumping this triggers a refetch (manual refresh). */
  refreshKey: number;
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Delivered":
      return "default";
    case "Pending":
      return "secondary";
    case "Failed":
      return "destructive";
    default:
      return "outline";
  }
}

function prettyPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

export function WebhookDeliveriesTable({
  clientId,
  status,
  sinceDays,
  limit,
  refreshKey,
}: WebhookDeliveriesTableProps) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WebhookDelivery | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDeliveries() {
      setLoading(true);
      setError(null);
      const since = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000).toISOString();
      const { data, error: apiError } = await api.GET("/admin/webhook-deliveries", {
        params: {
          query: {
            ClientId: clientId,
            Status: status,
            Since: since,
            Limit: limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load webhook deliveries.");
      } else {
        setDeliveries(data ?? []);
      }
      setLoading(false);
    }

    fetchDeliveries();
    return () => {
      cancelled = true;
    };
  }, [clientId, status, sinceDays, limit, refreshKey]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
        {loading ? (
          <Spinner className="size-4" />
        ) : (
          <span>
            {deliveries.length} {deliveries.length === 1 ? "delivery" : "deliveries"}
            {deliveries.length >= limit ? ` (showing first ${limit})` : ""}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
              <TableHead className="text-right">Code</TableHead>
              <TableHead>Last attempt</TableHead>
              <TableHead>Next attempt</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Payload</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deliveries.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-muted-foreground h-24 text-center">
                  No webhook deliveries found.
                </TableCell>
              </TableRow>
            ) : (
              deliveries.map((delivery) => (
                <TableRow key={delivery.id}>
                  <TableCell>
                    <Badge variant={statusVariant(delivery.status)}>{delivery.status}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{delivery.eventType}</TableCell>
                  <TableCell>
                    {delivery.organisationId ? (
                      <Link to={`/organisations/${delivery.organisationId}`}>
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {delivery.organisationId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {delivery.subscriptionId ? (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {delivery.subscriptionId.slice(0, 8)}...
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {delivery.attempts ?? 0}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {delivery.lastStatusCode == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      delivery.lastStatusCode
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {delivery.lastAttemptAt ? (
                      formatDateTime(delivery.lastAttemptAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {delivery.nextAttemptAt ? (
                      formatDateTime(delivery.nextAttemptAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(delivery.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setSelected(delivery)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Delivery payload</SheetTitle>
            <SheetDescription>
              {selected?.eventType} · {selected?.status}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="px-4 pb-4">
              <div className="mb-2 flex items-center justify-end">
                <CopyButton value={selected.payload} />
              </div>
              <pre className="max-h-[70vh] overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {prettyPayload(selected.payload)}
              </pre>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
